import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Trophy } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { web } from "../lib/api";
import { Loader, Empty, Avatar, height, fmt, shortDate } from "../components/app/kit";
import { HighlightList } from "../components/app/Highlights";
import { METRICS } from "../components/app/CombinePanel";

const BASE = process.env.REACT_APP_BACKEND_URL;
const rise = (i = 0) => ({ initial: { opacity: 1, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: 0.08 * i } });

const useNoIndex = (title) => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    const prev = document.title;
    if (title) document.title = title;
    return () => { document.head.removeChild(m); document.title = prev; };
  }, [title]);
};

const Fact = ({ label, value, testid }) => (value === null || value === undefined || value === "" ? null : (
  <div data-testid={testid} className="rounded-2xl border border-[#1E293B] bg-[#0F172A]/80 px-4 py-3">
    <p className="font-mono-gd text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</p>
    <p className="mt-1 font-display text-2xl font-bold uppercase text-white">{value}</p>
  </div>
));

const Combine = ({ combine }) => {
  const best = combine?.bests ?? {};
  const latest = combine?.latest ?? {};
  const rows = METRICS.filter(([k]) => best[k] != null || latest[k] != null);
  if (!rows.length) return null;
  return (
    <section data-testid="reel-combine" className="mt-16">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-2">
        <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white">Combine</h2>
        {combine.latest_date && <p className="font-mono-gd text-xs uppercase tracking-[0.2em] text-slate-400">Latest test {shortDate(combine.latest_date)}</p>}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {rows.map(([k, label, unit]) => (
          <div key={k} data-testid={`reel-combine-${k}`} className="rounded-2xl border border-[#1E293B] bg-[#0F172A] p-4">
            <p className="font-mono-gd text-[11px] uppercase tracking-[0.18em] text-slate-400">{label}</p>
            <p className="mt-2 font-mono-gd text-3xl font-bold text-[#39FF14]">{fmt(best[k] ?? latest[k])}<span className="ml-1 text-sm text-slate-400">{unit}</span></p>
            {latest[k] != null && best[k] != null && latest[k] !== best[k] && <p className="mt-1 text-xs text-slate-400">Latest {fmt(latest[k])}{unit}</p>}
            {best[k] != null && <p className="mt-1 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-slate-400"><Trophy className="h-3 w-3 text-[#39FF14]" />Best</p>}
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Reel() {
  const { token } = useParams();
  const { data, loading, error } = useApi(`/reels/${token}`, web);
  const p = data?.player ?? {};
  useNoIndex(p.name ? `${p.name} — Highlights | GAMEDAY` : "Highlights | GAMEDAY");

  if (loading) return <div className="min-h-screen bg-[#0A0E1A] pt-32"><Loader /></div>;
  if (error || !data) return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0E1A] px-6"><div data-testid="reel-unavailable" className="max-w-md"><Empty title="Page not available" text="This highlights page was turned off or the link changed. Ask the coach for a new link." /></div></div>
  );

  const positions = (Array.isArray(p.positions) && p.positions.length ? p.positions : [p.position]).filter(Boolean).join(" / ");
  const links = (data.highlights ?? []).map((h) => h?.url).filter(Boolean);
  const contact = data.contact?.email;
  const subject = encodeURIComponent(`Recruiting interest: ${p.name}`);

  return (
    <div data-testid="reel-page" className="min-h-screen bg-[#0A0E1A] text-white">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] bg-[radial-gradient(700px_circle_at_15%_0%,rgba(57,255,20,0.12),transparent_60%)]" />
      <main className="relative mx-auto max-w-6xl px-5 pb-20 pt-10 sm:px-8 sm:pt-16">
        <motion.p {...rise(0)} className="font-mono-gd text-xs uppercase tracking-[0.3em] text-[#39FF14]">{data.team_name ?? "GAMEDAY"} · Prospect</motion.p>
        <motion.div {...rise(1)} className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-end">
          <Avatar name={p.name ?? ""} src={data.has_photo ? `${BASE}/api/web/reels/${token}/photo` : null} size="h-28 w-28 sm:h-36 sm:w-36" />
          <div className="min-w-0">
            <h1 data-testid="reel-name" className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl">{p.name}</h1>
            <p className="mt-3 text-base text-slate-300">
              {p.jersey ? `#${p.jersey}` : ""}{positions ? ` · ${positions}` : ""}{p.grad_year ? ` · Class of ${p.grad_year}` : ""}
            </p>
          </div>
        </motion.div>
        <motion.div {...rise(2)} className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Fact label="Height" value={p.height_in ? height(p.height_in) : null} testid="reel-height" />
          <Fact label="Weight" value={p.weight_lb ? `${p.weight_lb} lb` : null} testid="reel-weight" />
          <Fact label="Grad year" value={p.grad_year} testid="reel-grad" />
          <Fact label="GPA" value={p.gpa != null ? fmt(p.gpa, 2) : null} testid="reel-gpa" />
        </motion.div>
        {contact && (
          <motion.a {...rise(3)} href={`mailto:${contact}?subject=${subject}`} data-testid="reel-contact-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-6 py-3 font-semibold text-[#0A0E1A] transition-transform hover:-translate-y-0.5">
            <Mail className="h-4 w-4" />Contact coach
          </motion.a>
        )}

        <section data-testid="reel-highlights" className="mt-16">
          <h2 className="mb-6 font-display text-4xl font-extrabold uppercase tracking-tight">Highlights</h2>
          <HighlightList links={links} emptyText="Videos will show up here once the coach adds them." />
        </section>
        <Combine combine={data.combine} />

        <footer className="mt-20 flex flex-wrap items-center justify-between gap-3 border-t border-[#1E293B] pt-6 text-xs text-slate-400">
          <span>Updated {shortDate(data.refreshed_at)}{data.contact?.name ? ` · Shared by ${data.contact.name}` : ""}</span>
          <Link to="/" className="font-mono-gd uppercase tracking-[0.2em] hover:text-[#39FF14]">Powered by GAMEDAY</Link>
        </footer>
      </main>
    </div>
  );
}
